import type { MouseEvent } from 'react';

import { isAuth0Enabled, performApiLogout, type UserSessionProviderContextProps } from '@libs/common-next';
import { evaluateSession, isLastActiveExpired, isUserLoggedIn } from '@libs/core';

import { requestExTokenVelocity } from './handleExchangeToken';

interface HandleServiceClickProps {
    authType?: UserSessionProviderContextProps['isAuthenticated'];
    event: MouseEvent | null;
    link?: string;
    shouldOpenInNewTab?: boolean;
    /** @deprecated Soft login removed; ignored. */
    isSoftAuthenticated?: boolean;
}

export function isServicesLink(link?: string | null): link is string {
    return Boolean(link?.includes('https://services'));
}

/** False when a revoked Auth0 session already started a top-level `/v2/logout`. */
export function shouldNavigateToServicesAfterSessionCheck({
    endedByInactivity,
    sessionStillValid,
    shouldOpenInNewTab,
}: {
    endedByInactivity: boolean;
    sessionStillValid: boolean;
    shouldOpenInNewTab: boolean;
}): boolean {
    return sessionStillValid || endedByInactivity || shouldOpenInNewTab;
}

async function mapFullLoggedUrl(url: URL, authType: UserSessionProviderContextProps['isAuthenticated']): Promise<URL> {
    if (isAuth0Enabled()) {
        return url;
    }

    const response = authType === 'velocity' ? await requestExTokenVelocity() : null;

    if (response && 'exchangeToken' in response && response.exchangeToken) {
        url.searchParams.set('extok', response.exchangeToken);
    }
    return url;
}

export async function handleServiceClick({
    authType,
    event,
    link,
    shouldOpenInNewTab = false,
}: Readonly<HandleServiceClickProps>) {
    if (!link) {
        return;
    }

    event?.preventDefault();

    const isGuest = !authType;

    if (isGuest || !isServicesLink(link)) {
        shouldOpenInNewTab ? window.open(link, '_blank') : window.location.assign(link);
        return;
    }

    let url = new URL(link);

    const endedByInactivity = isLastActiveExpired();
    const sessionStillValid = await evaluateSession({ skipRedirect: true, performApiLogout });

    if (sessionStillValid && isUserLoggedIn()) {
        url = await mapFullLoggedUrl(url, authType);
    }

    if (
        !shouldNavigateToServicesAfterSessionCheck({
            endedByInactivity,
            sessionStillValid,
            shouldOpenInNewTab,
        })
    ) {
        return;
    }

    shouldOpenInNewTab ? window.open(url.toString(), '_blank') : window.location.assign(url.toString());
}
